import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardButton, Dialog, Text, TextInput, useToaster } from '@gravity-ui/uikit';
import type { ManagerView } from '@odonix/contracts';
import { useManagersApi } from '../../lib/api/managers-context.js';
import { TenantsError } from '../../lib/api/tenants.js';
import { Field } from '../../ui/field.js';
import { managerAppUrl } from './labels.js';

/**
 * Заведение и правка менеджера одним окном. Пароля у менеджера нет — он
 * входит кодом на почту, — поэтому заведение — это почта, имя, телефон и
 * процент. Письмо не дошло — ссылку на кабинет видно сразу, её можно
 * скопировать и отправить другим путём.
 */

function fieldError(error: unknown, field: string): string | undefined {
  return error instanceof TenantsError ? error.fields?.[field] : undefined;
}

function percentOf(input: string): number | null {
  const normalized = input.trim().replace(',', '.');
  if (normalized === '') return 0;
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(normalized)) return null;
  const value = Number(normalized);
  return value <= 100 ? value : null;
}

export function ManagerFormDialog({
  open,
  manager,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Без менеджера — заведение нового. */
  manager: ManagerView | null;
  onClose: () => void;
  onSaved?: (manager: ManagerView) => void;
}) {
  const api = useManagersApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();
  const creating = manager === null;

  const [fullName, setFullName] = useState(manager?.fullName ?? '');
  const [email, setEmail] = useState(manager?.email ?? '');
  const [phone, setPhone] = useState(manager?.phone ?? '');
  const [rate, setRate] = useState('10');
  const [undelivered, setUndelivered] = useState(false);
  const percent = percentOf(rate);

  const mutation = useMutation({
    mutationFn: async (): Promise<{ saved: ManagerView; delivered: boolean }> => {
      if (creating) {
        const created = await api.create({
          email: email.trim(),
          fullName: fullName.trim(),
          ...(phone.trim() === '' ? {} : { phone: phone.trim() }),
          ...(percent === null || percent === 0 ? {} : { commissionPercent: percent }),
        });
        return { saved: created.manager, delivered: created.invitationDelivered };
      }
      const saved = await api.update(manager.id, {
        ...(fullName.trim() === manager.fullName ? {} : { fullName: fullName.trim() }),
        ...(email.trim().toLowerCase() === manager.email ? {} : { email: email.trim() }),
        ...(phone.trim() === (manager.phone ?? '') ? {} : { phone: phone.trim() }),
      });
      return { saved, delivered: true };
    },
    onSuccess: async ({ saved, delivered }) => {
      await queryClient.invalidateQueries({ queryKey: ['managers'] });
      await queryClient.invalidateQueries({ queryKey: ['managers-overview'] });
      await queryClient.invalidateQueries({ queryKey: ['manager-card', saved.id] });
      toaster.add({
        name: `manager-saved-${saved.id}`,
        title: creating ? `${saved.fullName} заведён` : 'Изменения сохранены',
        content: creating && delivered ? 'На почту ушло письмо со ссылкой на кабинет.' : undefined,
        theme: 'success',
        autoHiding: 5000,
      });
      onSaved?.(saved);
      if (creating && !delivered) {
        setUndelivered(true);
        return;
      }
      onClose();
    },
  });

  const unchanged =
    !creating &&
    fullName.trim() === manager.fullName &&
    email.trim().toLowerCase() === manager.email &&
    phone.trim() === (manager.phone ?? '');
  const invalid = fullName.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(email.trim()) || (creating && percent === null);
  const error = mutation.error;

  if (undelivered && creating) {
    return (
      <Dialog open={open} onClose={onClose} size="s" aria-label="Письмо не ушло">
        <Dialog.Header caption="Менеджер заведён, но письмо не ушло" />
        <Dialog.Body>
          <div className="flex flex-col gap-3">
            <Text color="secondary">
              Передайте менеджеру ссылку на кабинет другим путём: он войдёт по коду, который придёт на {email.trim()}.
            </Text>
            <div className="flex items-center gap-2 rounded-lg bg-[var(--g-color-base-generic)] px-3 py-2">
              <Text className="min-w-0 flex-1 break-all">{managerAppUrl()}</Text>
              <ClipboardButton text={managerAppUrl()} size="s" />
            </div>
          </div>
        </Dialog.Body>
        <Dialog.Footer textButtonApply="Готово" onClickButtonApply={onClose} />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} size="s" aria-label={creating ? 'Новый менеджер' : 'Менеджер'}>
      <Dialog.Header caption={creating ? 'Новый менеджер' : 'Данные менеджера'} />
      <Dialog.Body>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!invalid && !unchanged) mutation.mutate();
          }}
        >
          <Field label="Имя и фамилия" required error={fieldError(error, 'fullName')}>
            {(id) => <TextInput id={id} value={fullName} onUpdate={setFullName} autoFocus placeholder="Иванова Анна" />}
          </Field>
          <Field
            label="Почта"
            required
            hint={creating ? 'На неё придёт письмо со ссылкой на кабинет, потом — коды входа.' : 'Новая почта — новый адрес для кода входа.'}
            error={fieldError(error, 'email')}
          >
            {(id) => (
              <TextInput id={id} type="email" value={email} onUpdate={setEmail} placeholder="anna@odonix.ru" />
            )}
          </Field>
          <Field label="Телефон" error={fieldError(error, 'phone')}>
            {(id) => (
              <TextInput id={id} type="tel" value={phone} onUpdate={setPhone} placeholder="+7 900 000-00-00" />
            )}
          </Field>
          {creating ? (
            <Field
              label="Процент от подписки"
              hint="С каждой оплаты подписки его клиник, пока клиника за ним. Потом меняется в карточке."
              error={percent === null ? 'От 0 до 100, не точнее сотых' : fieldError(error, 'commissionPercent')}
            >
              {(id) => (
                <TextInput
                  id={id}
                  value={rate}
                  onUpdate={setRate}
                  endContent={<span className="pr-2 text-[var(--g-color-text-secondary)]">%</span>}
                  controlProps={{ inputMode: 'decimal' }}
                />
              )}
            </Field>
          ) : null}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply={creating ? 'Завести' : 'Сохранить'}
        textButtonCancel="Отмена"
        onClickButtonApply={() => {
          mutation.mutate();
        }}
        onClickButtonCancel={onClose}
        loading={mutation.isPending}
        propsButtonApply={{ disabled: invalid || unchanged }}
        {...(error === null
          ? {}
          : {
              errorText: error instanceof TenantsError ? error.message : 'Не удалось сохранить. Повторите',
              showError: true,
            })}
      />
    </Dialog>
  );
}
